(() => {
  const existingRuntime = window.FourPawSafeWalkTracking;
  if (existingRuntime) {
    existingRuntime.scan(document);
    return;
  }

  const instances = new WeakMap();
  const eventLog = [];
  const publishQueue = [];
  const maxPublishAttempts = 40;
  let publishTimer = null;
  let publishAttempts = 0;

  const flushPublishQueue = () => {
    publishTimer = null;
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') {
      publishAttempts += 1;
      if (publishQueue.length && publishAttempts < maxPublishAttempts) {
        publishTimer = window.setTimeout(flushPublishQueue, 250);
      } else {
        publishQueue.length = 0;
      }
      return;
    }
    publishAttempts = 0;
    while (publishQueue.length) {
      const payload = publishQueue.shift();
      try {
        publish.call(window.Shopify.analytics, 'fourpaw:safewalk_funnel', payload);
      } catch (_) {
        return;
      }
    }
  };

  const publish = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'fourpaw_safewalk_funnel', ...payload });
    publishQueue.push(payload);
    if (!publishTimer) flushPublishQueue();
  };

  const cartUpdateUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
  };

  class SafeWalkTracking {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.startedAt = performance.now();
      this.maxScrollProgress = 0;
      this.currentPackage = root.dataset.defaultPackageKind || '';
      this.currentVariantId = root.dataset.currentVariantId || '';
      this.giftArchitecture = root.dataset.giftMode || '';
      this.previewSeen = false;
      this.offerReached = false;
      this.startedFields = new Set();
      this.visiblePreviews = new Set();
      this.observedPreviews = new WeakSet();
      this.pendingAttributes = {};
      this.sentAttributes = {};
      this.attributeTimer = null;
      this.attributeRequest = null;
      this.attributeRetryCount = 0;
      this.debug = new URLSearchParams(window.location.search).get('fp_tracking_debug') === '1';
      this.production = root.dataset.swv5TrackingThemeRole === 'main'
        && !window.Shopify?.designMode
        && !new URLSearchParams(window.location.search).has('preview_theme_id');
      this.onScroll = this.onScroll.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPackageState = this.onPackageState.bind(this);
      this.onCartResult = this.onCartResult.bind(this);

      const signal = this.abortController.signal;
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      root.addEventListener('focusin', this.onFocusIn, { signal });
      root.addEventListener('input', this.onInput, { signal });
      root.addEventListener('change', this.onChange, { signal });
      root.addEventListener('swv5:package-change', this.onPackageState, { signal });
      root.addEventListener('swv5:cart-add-result', this.onCartResult, { signal });
      document.addEventListener('click', this.onClick, { capture: true, signal });

      this.observeOffer();
      this.observePreviews();
      this.onScroll();
      this.emit('pdp_view');
    }

    basePayload(stage) {
      const productId = Number(this.root.dataset.currentProductId);
      const variantId = Number(this.currentVariantId);
      return {
        schema_version: 1,
        stage,
        cohort: this.root.dataset.swv5TrackingCohort || '',
        tracking_revision: this.root.dataset.swv5TrackingRevision || '',
        theme_id: this.root.dataset.swv5TrackingThemeId || '',
        theme_role: this.root.dataset.swv5TrackingThemeRole || '',
        product_id: Number.isFinite(productId) ? productId : this.root.dataset.currentProductId,
        product_handle: this.root.dataset.currentProductHandle || '',
        variant_id: Number.isFinite(variantId) ? variantId : this.currentVariantId,
        currency: this.root.dataset.currency || '',
        package_kind: this.currentPackage,
        preview_seen: this.previewSeen,
        elapsed_seconds: Math.max(0, Math.round((performance.now() - this.startedAt) / 1000)),
        scroll_depth_band: this.scrollDepthBand(),
        page_path: window.location.pathname
      };
    }

    emit(stage, detail = {}) {
      const payload = { ...this.basePayload(stage), ...detail };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null || payload[key] === '') delete payload[key];
      });
      eventLog.push(payload);
      document.dispatchEvent(new CustomEvent('fourpaw:safewalk-funnel-debug', { detail: payload }));
      if (this.debug) console.info('[FourPaw funnel]', payload);
      if (this.production) publish(payload);
    }

    onScroll() {
      const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const progress = height ? Math.min(1, (window.scrollY + window.innerHeight) / height) : 0;
      this.maxScrollProgress = Math.max(this.maxScrollProgress, progress);
    }

    scrollDepthBand() {
      const percent = Math.round(this.maxScrollProgress * 100);
      if (percent <= 25) return '0_25';
      if (percent <= 50) return '26_50';
      if (percent <= 75) return '51_75';
      return '76_100';
    }

    personalizationField(target) {
      if (!(target instanceof HTMLInputElement) || !target.closest('.swv51-personalization')) return '';
      const key = target.dataset.safewalkPersonalizationKey || '';
      if (key === 'dog_name' || target.name.includes('שם הכלב')) return 'dog_name';
      if (key === 'phone' || target.name.includes('מספר טלפון')) return 'phone';
      return '';
    }

    dogNameInput() {
      return this.root.querySelector(
        '.swv51-personalization input[data-safewalk-personalization-key="dog_name"], .swv51-personalization input[name*="שם הכלב"]'
      );
    }

    onFocusIn(event) {
      const field = this.personalizationField(event.target);
      if (!field || this.startedFields.has(field)) return;
      this.startedFields.add(field);
      this.emit('personalization_start', { field });
    }

    onInput(event) {
      const field = this.personalizationField(event.target);
      if (!field) return;
      if (!this.startedFields.has(field)) {
        this.startedFields.add(field);
        this.emit('personalization_start', { field });
      }
      if (field === 'dog_name') window.requestAnimationFrame(() => this.maybeMarkPreviewSeen());
    }

    onChange(event) {
      const packageInput = event.target.closest?.('[data-package-input]');
      if (!packageInput || !this.root.contains(packageInput)) return;
      this.currentPackage = packageInput.value || this.currentPackage;
      this.emit('package_selected');
      this.queueCartAttributes(this.attributionAttributes());
    }

    onClick(event) {
      const addButton = event.target.closest?.('[data-swv5-submit]');
      if (addButton && this.root.contains(addButton)) {
        const surface = addButton.closest('[data-swv51-sticky-actions]') ? 'sticky' : 'buy_box';
        this.emit('add_to_cart_attempt', { surface });
        this.queueCartAttributes(this.attributionAttributes());
      }

      const checkout = event.target.closest?.('a[href*="/checkout"], button[name="checkout"], [data-checkout-button]');
      if (checkout) this.emit('checkout_click');
    }

    onPackageState(event) {
      if (event.detail?.kind) this.currentPackage = event.detail.kind;
      if (event.detail?.harnessVariantId) this.currentVariantId = event.detail.harnessVariantId;
    }

    onCartResult(event) {
      const ok = event.detail?.ok === true;
      const totalCents = Number(event.detail?.expectedTotalCents);
      this.emit(ok ? 'add_to_cart_success' : 'add_to_cart_error', {
        package_kind: event.detail?.package || this.currentPackage,
        http_status: Number(event.detail?.status) || 0,
        gift_architecture: event.detail?.giftArchitecture || this.giftArchitecture,
        harness_quantity: Number(event.detail?.harnessQuantity) || 1,
        cart_line_count: Number(event.detail?.cartLineCount) || undefined,
        value: Number.isFinite(totalCents) ? totalCents / 100 : undefined
      });
      if (ok) this.queueCartAttributes(this.attributionAttributes());
    }

    observeOffer() {
      const targets = Array.from(this.root.querySelectorAll('[data-package-selector]'));
      if (!targets.length || !('IntersectionObserver' in window)) return;
      this.offerObserver = new IntersectionObserver((entries) => {
        if (this.offerReached || !entries.some((entry) => entry.isIntersecting)) return;
        this.offerReached = true;
        this.emit('offer_reached');
        this.queueCartAttributes(this.attributionAttributes());
        this.offerObserver.disconnect();
      }, { threshold: 0.2 });
      targets.forEach((target) => this.offerObserver.observe(target));
    }

    observePreviews() {
      if (!('IntersectionObserver' in window)) return;
      this.previewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) this.visiblePreviews.add(entry.target);
          else this.visiblePreviews.delete(entry.target);
        });
        this.maybeMarkPreviewSeen();
      }, { threshold: [0, 0.45, 0.75] });
      this.scanPreviewNodes(this.root);
      this.previewMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) this.scanPreviewNodes(node);
        }));
      });
      this.previewMutationObserver.observe(this.root, { childList: true, subtree: true });
    }

    scanPreviewNodes(scope) {
      const nodes = [];
      if (scope.matches?.('[data-swv51-tag-preview], [data-swv6-tag-projection-owner]')) nodes.push(scope);
      scope.querySelectorAll?.('[data-swv51-tag-preview], [data-swv6-tag-projection-owner]').forEach((node) => nodes.push(node));
      nodes.forEach((node) => {
        if (this.observedPreviews.has(node)) return;
        this.observedPreviews.add(node);
        this.previewObserver.observe(node);
      });
    }

    maybeMarkPreviewSeen() {
      if (this.previewSeen || !(this.dogNameInput()?.value || '').trim() || !this.visiblePreviews.size) return;
      this.previewSeen = true;
      this.emit('personalized_preview_seen');
      this.queueCartAttributes(this.attributionAttributes());
    }

    attributionAttributes() {
      const attributes = {
        __fp_theme_cohort: this.root.dataset.swv5TrackingCohort || '',
        __fp_theme_id: this.root.dataset.swv5TrackingThemeId || '',
        __fp_safewalk_package: this.currentPackage || ''
      };
      if (this.previewSeen) attributes.__fp_safewalk_preview_seen = '1';
      return attributes;
    }

    queueCartAttributes(attributes) {
      if (!this.production || this.giftArchitecture === 'kaching_gift_pack_v1') return;
      Object.entries(attributes).forEach(([key, value]) => {
        const normalized = String(value || '');
        if (normalized && this.sentAttributes[key] !== normalized) this.pendingAttributes[key] = normalized;
      });
      if (!Object.keys(this.pendingAttributes).length || this.attributeRequest) return;
      window.clearTimeout(this.attributeTimer);
      this.attributeTimer = window.setTimeout(() => this.flushCartAttributes(), 120);
    }

    async flushCartAttributes() {
      if (this.attributeRequest || !Object.keys(this.pendingAttributes).length) return;
      const attributes = { ...this.pendingAttributes };
      this.pendingAttributes = {};
      this.attributeRequest = fetch(cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes })
      });
      try {
        const response = await this.attributeRequest;
        if (!response.ok) throw new Error('cart-attribution');
        Object.assign(this.sentAttributes, attributes);
        this.attributeRetryCount = 0;
      } catch (_) {
        this.attributeRetryCount += 1;
        if (this.attributeRetryCount <= 2) Object.assign(this.pendingAttributes, attributes);
      } finally {
        this.attributeRequest = null;
        if (Object.keys(this.pendingAttributes).length) {
          this.attributeTimer = window.setTimeout(() => this.flushCartAttributes(), 1200);
        }
      }
    }

    destroy() {
      this.abortController.abort();
      this.offerObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.previewMutationObserver?.disconnect();
      window.clearTimeout(this.attributeTimer);
    }
  }

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope.matches?.('[data-swv5-tracking-cohort]')) roots.push(scope);
    scope.querySelectorAll?.('[data-swv5-tracking-cohort]').forEach((root) => roots.push(root));
    return roots;
  };

  const runtime = {
    events: eventLog,
    scan(scope = document) {
      rootsWithin(scope).forEach((root) => {
        if (!instances.has(root)) instances.set(root, new SafeWalkTracking(root));
      });
    },
    destroy(scope) {
      rootsWithin(scope).forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });
    }
  };

  window.FourPawSafeWalkTracking = runtime;
  runtime.scan(document);
  document.addEventListener('shopify:section:load', (event) => runtime.scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => runtime.destroy(event.target));
})();
